import { create } from 'zustand';
import type {
  ServiceOrder,
  ServiceFilters,
  ListServiceOrdersRequest,
} from '../types/service';
import {
  getJSON,
  postJSON,
  NOT_MODIFIED,
  ApiError,
  AuthRequiredError,
} from '../lib/apiClient';
import { useAuthStore } from './authStore';

// ─────────────────────────────────────────────────────────────────────────────
// Service Manager store.
//
// Two kinds of filter live here and they are not the same thing:
//
//   `filters`      go to the SERVER (branch, order type, date, categories).
//                  Changing one re-queries — the legacy screen's four dropdowns.
//   `search` /     stay on the CLIENT. They narrow what is already on screen and
//   `lateOnly`     must feel instant as the cook types; a round trip per
//                  keystroke on a kitchen tablet does not.
//
// Print is here rather than in the component because a print is a request with
// three outcomes (sent / refused / auth gone) and the screen needs to say which
// — a print button that silently does nothing is the worst thing on this page.
// ─────────────────────────────────────────────────────────────────────────────

export type ServiceStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface ServiceToast {
  id: number;
  tone: 'ok' | 'error';
  message: string;
}

/** Which button on which card is mid-request, so it can show a spinner. */
export type PrintKind = 'receipt' | 'kitchen';

interface ServiceState {
  filters: ListServiceOrdersRequest;
  filterOptions: ServiceFilters | null;
  orders: ServiceOrder[];
  serverTime: string | null;
  status: ServiceStatus;
  error: string | null;

  /** Client-side narrowing — see the note above. */
  search: string;
  lateOnly: boolean;

  toasts: ServiceToast[];
  /** `${orderId}:${kind}` for every print in flight. */
  printing: Set<string>;

  setFilters: (patch: Partial<ListServiceOrdersRequest>) => void;
  setSearch: (value: string) => void;
  setLateOnly: (value: boolean) => void;
  resetFilters: () => void;
  loadFilterOptions: () => Promise<void>;
  refresh: () => Promise<void>;
  markLineReady: (lineId: number) => Promise<void>;
  markOrderReady: (orderId: number) => Promise<void>;
  print: (orderId: number, kind: PrintKind) => Promise<void>;
  dismissToast: (id: number) => void;
}

function todayLocal(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function initialFilters(): ListServiceOrdersRequest {
  return {
    startDate: todayLocal(),
    endDate: todayLocal(),
    dateField: 'order',
  };
}

let toastSeq = 0;

export const useServiceStore = create<ServiceState>((set, get) => ({
  filters: initialFilters(),
  filterOptions: null,
  orders: [],
  serverTime: null,
  status: 'idle',
  error: null,
  search: '',
  lateOnly: false,
  toasts: [],
  printing: new Set<string>(),

  setFilters: (patch) => {
    set((s) => ({ filters: { ...s.filters, ...patch } }));
  },

  setSearch: (value) => set({ search: value }),
  setLateOnly: (value) => set({ lateOnly: value }),

  resetFilters: () => {
    set({ filters: initialFilters(), search: '', lateOnly: false });
    void get().refresh();
  },

  loadFilterOptions: async () => {
    try {
      const result = await getJSON<ServiceFilters>('/api/v1/service/filters');
      if (result === NOT_MODIFIED) return;
      set({ filterOptions: result });
    } catch (err) {
      if (err instanceof AuthRequiredError) {
        useAuthStore.getState().onAuthExpired();
      } else {
        set({ error: stringifyError(err) });
      }
    }
  },

  refresh: async () => {
    const { filters, status } = get();
    if (status === 'idle') set({ status: 'loading' });

    try {
      const url = buildOrdersUrl(filters);
      const result = await getJSON<{ orders: ServiceOrder[]; serverTime: string }>(url);
      if (result === NOT_MODIFIED) {
        set((s) => (s.status !== 'ready' ? { status: 'ready' } : s));
        return;
      }
      set({
        orders: result.orders,
        serverTime: result.serverTime,
        status: 'ready',
        error: null,
      });
    } catch (err) {
      if (err instanceof AuthRequiredError) {
        useAuthStore.getState().onAuthExpired();
      } else {
        set({ status: 'error', error: stringifyError(err) });
      }
    }
  },

  // Both bumps repaint before the round trip: the cook taps and looks away, and
  // a card that only clears when the network says so feels broken on a tablet.
  //
  // Rollback restores the SNAPSHOT rather than re-fetching. It has to: /orders
  // is ETag-cached, so when a bump fails the server's snapshot is unchanged, the
  // refresh comes back 304, and the store keeps whatever it is holding — which
  // would be the optimistic state we were trying to undo. A line would sit there
  // struck through and cold until something unrelated changed the board.
  markLineReady: async (lineId) => {
    const before = get().orders;
    set((s) => ({
      orders: s.orders.map((o) => ({
        ...o,
        lines: o.lines.map((l) =>
          l.id === lineId ? { ...l, isReady: true, kitchenStatus: 3 } : l,
        ),
      })),
    }));
    try {
      await postJSON(`/api/v1/service/lines/${lineId}/ready`);
      await get().refresh();
    } catch (err) {
      set({ orders: before });
      if (err instanceof AuthRequiredError) {
        useAuthStore.getState().onAuthExpired();
      } else {
        pushToast(set, 'error', stringifyError(err));
      }
    }
  },

  markOrderReady: async (orderId) => {
    const before = get().orders;
    set({ orders: before.filter((o) => o.id !== orderId) });
    try {
      await postJSON(`/api/v1/service/orders/${orderId}/ready`);
      await get().refresh();
    } catch (err) {
      set({ orders: before });
      if (err instanceof AuthRequiredError) {
        useAuthStore.getState().onAuthExpired();
      } else {
        pushToast(set, 'error', stringifyError(err));
      }
    }
  },

  print: async (orderId, kind) => {
    const key = `${orderId}:${kind}`;
    if (get().printing.has(key)) return; // double-tap on a laggy tablet
    set((s) => ({ printing: new Set(s.printing).add(key) }));

    const path =
      kind === 'kitchen'
        ? `/api/v1/service/orders/${orderId}/kitchen-print`
        : `/api/v1/service/orders/${orderId}/print`;

    try {
      const result = await postJSON<{ sent: number; printers: string[] }>(path);
      const where = result.printers.length > 0 ? ` → ${result.printers.join(', ')}` : '';
      pushToast(set, 'ok', `#${orderId}${where}`);
    } catch (err) {
      if (err instanceof AuthRequiredError) {
        useAuthStore.getState().onAuthExpired();
      } else {
        pushToast(set, 'error', printErrorMessage(err));
      }
    } finally {
      set((s) => {
        const next = new Set(s.printing);
        next.delete(key);
        return { printing: next };
      });
    }
  },

  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

type SetState = (fn: (s: ServiceState) => Partial<ServiceState>) => void;

function pushToast(set: SetState, tone: 'ok' | 'error', message: string): void {
  const id = ++toastSeq;
  set((s) => ({ toasts: [...s.toasts, { id, tone, message }] }));
  // Errors sit longer: someone has to read them and decide what to do.
  setTimeout(
    () => useServiceStore.setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
    tone === 'error' ? 8000 : 3500,
  );
}

/**
 * Turn a failed print into something a non-technical user can act on. The API
 * answers 502 PRINT_NOT_SENT with the reason SCPS gave, and that reason is
 * usually "this branch has no printer configured" — which is a call to the
 * office, not a retry.
 */
function printErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: string; error?: string } | null;
    if (body?.detail) return body.detail;
    if (err.status === 404) return 'That order no longer exists.';
  }
  return stringifyError(err);
}

function buildOrdersUrl(filters: ListServiceOrdersRequest): string {
  const params = new URLSearchParams();
  params.set('startDate', filters.startDate);
  params.set('endDate', filters.endDate);
  params.set('dateField', filters.dateField);
  if (filters.branchIds && filters.branchIds.length > 0) {
    params.set('branchIds', filters.branchIds.join(','));
  }
  if (filters.categoryIds && filters.categoryIds.length > 0) {
    params.set('categoryIds', filters.categoryIds.join(','));
  }
  if (filters.orderType !== undefined && filters.orderType !== 'all') {
    params.set('orderType', String(filters.orderType));
  }
  return `/api/v1/service/orders?${params.toString()}`;
}

function stringifyError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
